import { useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress, InputAdornment, Typography } from '@mui/material';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import styled from 'styled-components';
import { AGENT_SESSIONS } from '../../../constants/queryKeys';
import { Flex, FlexColumn } from '../../Flex';
import OverflowTooltip from '../../OverflowTooltip';
import SvgIcon from '../../SvgIcon/SvgIcon';
import TextField from '../../TextField';
import IconButton from '../../IconButton';
import { ReactComponent as SearchIcon } from '../../../assets/search.svg';
import { ReactComponent as MoreIcon } from '../../../assets/16x16/more.svg';
import { ReactComponent as SlackIcon } from '../../../assets/16x16/slack-mono.svg';
import { fetchSessionHistory } from '../../../services/api';
import { localizeDate } from '../../../utils/datetime';
import { HISTORY_PAGE_SIZE, SESSION_ACTIVE_STATUSES } from '../constants';
import { HistoryData } from '../hooks/useChatHistory';
import type {
  SessionStatus,
  SessionHistoryItemResponse,
  SessionHistoryResponse,
} from '../../../types';
import InlineTitleEdit from './InlineTitleEdit';
import ChatRowMenu from './ChatRowMenu';

const ItemsList = styled(FlexColumn)`
  gap: 8px;
  padding: 16px 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const HistoryItem = styled(Flex)<{ $isActive?: boolean }>`
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.palette.common.gray10 : 'none'};
  padding: 12px 24px;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.gray2};
  transition: background 0.15s;
  flex-shrink: 0;
  width: 100%;
  min-width: 0;
  align-items: center;

  & .row-menu {
    opacity: 0;
    transition: opacity 150ms;
  }

  &:hover {
    background: ${({ theme }) => theme.palette.common.gray10};
  }

  &:hover .row-menu {
    opacity: 1;
  }
`;

const TITLE_POLL_INTERVAL_MS = 2000;

const isSessionActive = (session: { status: SessionStatus }) =>
  SESSION_ACTIVE_STATUSES.includes(session.status);

type DotVariant = 'active' | 'waiting' | 'idle';

const STATUS_STALE_MS = 24 * 60 * 60 * 1000;

// `waiting_for_input` is shown blue while fresh, but once a day passes with no
// activity it reads as idle (the agent parks on a prompt at the end of every
// turn, so a stale wait is effectively a finished conversation).
const getDotVariant = (chat: {
  status?: SessionStatus;
  updatedAt?: string;
}): DotVariant => {
  if (chat.status === 'waiting_for_input') {
    const updatedMs = chat.updatedAt ? new Date(chat.updatedAt).getTime() : 0;
    return Date.now() - updatedMs >= STATUS_STALE_MS ? 'idle' : 'waiting';
  }
  if (chat.status && SESSION_ACTIVE_STATUSES.includes(chat.status)) {
    return 'active';
  }
  return 'idle';
};

const StatusDot = styled.span<{ $variant: DotVariant }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $variant, theme }) =>
    $variant === 'active'
      ? theme.palette.warning.main
      : $variant === 'waiting'
      ? theme.palette.common.blue
      : 'transparent'};
  border: 1px solid
    ${({ $variant, theme }) =>
      $variant === 'active'
        ? theme.palette.warning.main
        : $variant === 'waiting'
        ? theme.palette.common.blue
        : theme.palette.common.gray12};
`;

const TriggerSourceIcon = ({ source }: { source?: string }) => {
  if (source === 'slack') {
    return (
      <SvgIcon
        width={14}
        height={14}
        strokeColor="gray4"
        style={{ marginLeft: 'auto', flexShrink: 0 }}
      >
        <SlackIcon />
      </SvgIcon>
    );
  }
  return null;
};

const GroupLabel = styled(Typography)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray4};
  padding: 8px 24px;
`;

const getTimeGroup = (time: string): string => {
  const date = localizeDate(time);
  const now = localizeDate(new Date());

  if (date.isSame(now, 'day')) return 'Today';
  if (date.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  if (date.isSame(now, 'week')) return 'This week';
  if (date.isSame(now, 'month')) return 'This month';
  return 'Older';
};

type HistoryEntry = {
  id: string;
  title: string;
  time: string;
  status?: SessionStatus;
  updatedAt?: string;
  triggerSource?: string;
  pinnedAt?: string | null;
};

type ChatGroup = {
  key: string;
  label: string;
  chats: HistoryEntry[];
};

const groupChats = (chats: HistoryEntry[]): ChatGroup[] => {
  const groups: ChatGroup[] = [];

  for (const chat of chats) {
    const label = getTimeGroup(chat.time);
    const lastGroup = groups[groups.length - 1];

    if (lastGroup?.label === label) {
      lastGroup.chats.push(chat);
    } else {
      groups.push({ key: `time:${label}`, label, chats: [chat] });
    }
  }

  return groups;
};

const toEntry = (session: SessionHistoryItemResponse): HistoryEntry => ({
  id: session.session_id,
  title: session.title ?? '',
  time: session.created_at,
  status: session.status,
  updatedAt: session.updated_at,
  triggerSource: session.trigger_source,
  pinnedAt: session.pinned_at,
});

type Props = {
  onSelectHistory: (data: HistoryData) => void;
  onThreadLoadStart?: () => void;
  activeThreadId?: string;
};

const ChatHistoryPanel = ({
  onSelectHistory,
  onThreadLoadStart,
  activeThreadId,
}: Props) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  const nextSessionPageParam = (
    lastPage: SessionHistoryResponse,
    allPages: SessionHistoryResponse[],
  ) => {
    const loadedCount = allPages.reduce(
      (sum, page) => sum + page.sessions.length,
      0,
    );
    return lastPage.sessions.length === HISTORY_PAGE_SIZE
      ? loadedCount
      : undefined;
  };

  const refetchWhenActive = (
    data?: InfiniteData<SessionHistoryResponse>,
  ): number | false => {
    const hasActive = data?.pages.some((page) =>
      page.sessions.some(isSessionActive),
    );
    return hasActive ? TITLE_POLL_INTERVAL_MS : false;
  };

  const pinnedQuery = useInfiniteQuery({
    queryKey: [AGENT_SESSIONS, 'pinned'],
    queryFn: ({ pageParam = 0 }) =>
      fetchSessionHistory(HISTORY_PAGE_SIZE, pageParam, true),
    getNextPageParam: nextSessionPageParam,
    refetchInterval: refetchWhenActive,
  });

  const unpinnedQuery = useInfiniteQuery({
    queryKey: [AGENT_SESSIONS, 'unpinned'],
    queryFn: ({ pageParam = 0 }) =>
      fetchSessionHistory(HISTORY_PAGE_SIZE, pageParam, false),
    getNextPageParam: nextSessionPageParam,
    refetchInterval: refetchWhenActive,
  });

  const activeSessionQuery = pinnedQuery.hasNextPage
    ? pinnedQuery
    : unpinnedQuery;
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = activeSessionQuery;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string>();
  const [optimisticThreadId, setOptimisticThreadId] = useState<string>();

  useEffect(() => {
    if (activeThreadId === optimisticThreadId) {
      setOptimisticThreadId(undefined);
    }
  }, [activeThreadId, optimisticThreadId]);

  const effectiveActiveId = optimisticThreadId ?? activeThreadId;

  const handleItemClick = (threadId: string) => {
    setOptimisticThreadId(threadId);
    onThreadLoadStart?.();
    onSelectHistory({ threadId, messages: [] });
  };

  const pinnedChats = useMemo<HistoryEntry[]>(
    () =>
      pinnedQuery.data?.pages.flatMap((page) =>
        page.sessions.filter((session) => session.title).map(toEntry),
      ) ?? [],
    [pinnedQuery.data],
  );

  const unpinnedChats = useMemo<HistoryEntry[]>(
    () =>
      unpinnedQuery.data?.pages.flatMap((page) =>
        page.sessions.filter((session) => session.title).map(toEntry),
      ) ?? [],
    [unpinnedQuery.data],
  );

  const matchesSearch = (chat: HistoryEntry) => {
    const trimmed = search.trim().toLowerCase();
    return !trimmed || chat.title.toLowerCase().includes(trimmed);
  };

  const sections = useMemo<ChatGroup[]>(() => {
    const pinned = pinnedChats
      .filter(matchesSearch)
      .sort((a, b) => (b.pinnedAt ?? '').localeCompare(a.pinnedAt ?? ''));
    const rest = unpinnedChats
      .filter(matchesSearch)
      .sort((a, b) => b.time.localeCompare(a.time));
    const result: ChatGroup[] = [];
    if (pinned.length) {
      result.push({ key: 'pinned', label: 'Pinned', chats: pinned });
    }
    result.push(...groupChats(rest));
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedChats, unpinnedChats, search]);

  return (
    <>
      <Flex sx={{ px: 3, pt: 2, flexShrink: 0 }}>
        <TextField
          fullWidth
          placeholder="Search"
          variant="outlined"
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          size="medium"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Flex>
      <ItemsList>
        {sections.map((group) => (
          <FlexColumn key={group.key}>
            <GroupLabel>{group.label}</GroupLabel>
            {group.chats.map((chat) => (
              <HistoryItem
                key={chat.id}
                $isActive={chat.id === effectiveActiveId}
                onClick={() => handleItemClick(chat.id)}
                sx={{ gap: 1 }}
              >
                <StatusDot $variant={getDotVariant(chat)} />
                <Flex sx={{ flex: 1, minWidth: 0 }}>
                  {editingId === chat.id ? (
                    <InlineTitleEdit
                      sessionId={chat.id}
                      currentTitle={chat.title}
                      onDone={() => setEditingId(undefined)}
                    />
                  ) : (
                    <OverflowTooltip
                      typographySx={{ fontSize: 'inherit', color: 'inherit' }}
                    >
                      {chat.title}
                    </OverflowTooltip>
                  )}
                </Flex>
                {editingId !== chat.id && (
                  <TriggerSourceIcon source={chat.triggerSource} />
                )}
                {editingId !== chat.id && (
                  <ChatRowMenu
                    sessionId={chat.id}
                    isPinned={!!chat.pinnedAt}
                    onRename={() => setEditingId(chat.id)}
                    menuButton={
                      <IconButton
                        className="row-menu"
                        size="small"
                        sx={{ flexShrink: 0 }}
                      >
                        <SvgIcon width={16}>
                          <MoreIcon />
                        </SvgIcon>
                      </IconButton>
                    }
                  />
                )}
              </HistoryItem>
            ))}
          </FlexColumn>
        ))}
        {hasNextPage && (
          <Flex
            ref={sentinelRef}
            sx={{ justifyContent: 'center', py: 2, flexShrink: 0 }}
          >
            {isFetchingNextPage && <CircularProgress size={20} />}
          </Flex>
        )}
      </ItemsList>
    </>
  );
};

export default ChatHistoryPanel;
